/**
 * VELOCITY — motion system (task B4)
 *
 * Provides the three shared movements in the theme:
 *   - scroll reveal (fade-up via IntersectionObserver)
 *   - drawer slide  (CSS-driven; this file only manages state + a11y)
 *   - image hover-swap (desktop pointers only)
 *
 * The reduced-motion kill-switch is enforced in three places, deliberately:
 *   1. --motion-scale: 0 in critical-css.liquid zeroes every token-driven
 *      transition duration.
 *   2. The global @media block in base.css flattens anything that slipped past.
 *   3. This file skips the observer entirely and reveals everything up front,
 *      so no element is ever left invisible waiting for an animation that will
 *      not run.
 *
 * Point 3 matters: a CSS-only kill-switch that sets duration to 0 still leaves
 * `opacity: 0` painted until the class flips. Content must never depend on
 * motion to become visible.
 */
(function () {
  'use strict';

  var V = window.Velocity;
  if (!V) return;

  var REVEAL_SELECTOR = '[data-reveal]';
  var REVEALED = 'is-revealed';

  /**
   * Reveal-on-scroll. Elements opt in with data-reveal; an optional
   * data-reveal-delay (ms, capped) staggers items within a group.
   */
  function initReveal(root) {
    var scope = root || document;
    var items = Array.prototype.slice.call(scope.querySelectorAll(REVEAL_SELECTOR));
    if (!items.length) return;

    // Reveal immediately and bail when motion is off, the API is missing, or
    // the merchant disabled animations in theme settings.
    if (V.prefersReducedMotion() || !('IntersectionObserver' in window) || !V.motionEnabled) {
      items.forEach(function (el) {
        el.classList.add(REVEALED);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          // Cap the stagger so a long list cannot delay content indefinitely.
          var delay = Math.min(parseInt(el.dataset.revealDelay, 10) || 0, 400);
          if (delay) {
            setTimeout(function () {
              el.classList.add(REVEALED);
            }, delay);
          } else {
            el.classList.add(REVEALED);
          }
          observer.unobserve(el);
        });
      },
      // Fire slightly before the element reaches the viewport so the movement
      // has finished by the time it is properly in view.
      { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });

    return observer;
  }

  /**
   * Image hover-swap for product cards (used by F1).
   * Pointer-capable devices only — on touch, a "hover" is a tap and swapping
   * the image on tap fights the link navigation.
   */
  function initHoverSwap(root) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var scope = root || document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-hover-swap]'),
      function (el) {
        el.classList.add('has-hover-swap');
      }
    );
  }

  /* Re-run after Section Rendering API swaps (facets, load-more, cart drawer)
   * so newly injected markup animates like the rest of the page. */
  function refresh(root) {
    initReveal(root);
    initHoverSwap(root);
  }

  function boot() {
    refresh(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Sections re-render in the Theme Editor without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    refresh(e.target);
  });

  V.motion = { refresh: refresh, initReveal: initReveal, initHoverSwap: initHoverSwap };
})();
